import React, { useCallback, useEffect, useReducer, useState } from 'react';
import {
  Asset,
  AssetId,
  getCategorizedGroups,
  Group,
  GroupId,
  isCategorized,
  isLoading,
  isUnloaded,
  UncategorizedGroup,
  useGrouping,
  undecidedId,
  isLoaded,
  Grouping as GroupingType,
  UnnamedGroup,
  unnamedId,
  isUnnamedGroup,
} from 'hooks/useGrouping';

import GroupingTabList from 'components/features/grouping/GroupingTabList';
import GroupingTabSection from 'components/features/grouping/GroupingTabSection';
import Loading from 'components/commons/loading/Loading';
import TWEEN from '@tweenjs/tween.js';
import { useRouting } from 'hooks/useRouting';
import { useLocation } from 'react-router-dom';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { usePrevious } from 'hooks/usePrevious';
import { useGlobalMenuContext } from 'contexts/GlobalMenuContext';

const useUnnamedGroup = (groupingState: GroupingType): UnnamedGroup | null => {
  const { search } = useLocation();
  const id = /^\?id=(\d|10)$/.exec(search)?.[1];
  if (!id) return null;
  if (!isLoaded(groupingState))
    return {
      id: unnamedId(id),
      name: '',
    };
  return null;
};

export const GROUP_DELETION_STATE = {
  GROUP_DELETE_READY: 'GROUP_DELETE_READY',
  GROUP_DELETE_ANIMATION_STARTED: 'GROUP_DELETE_ANIMATION_STARTED',
  GROUP_DELETE_ANIMATION_FINISHED: 'GROUP_DELETE_ANIMATION_FINISHED',
  GROUP_DELETE_FINISHED: 'GROUP_DELETE_FINISHED',
} as const;
export type GroupDeletionState = typeof GROUP_DELETION_STATE[keyof typeof GROUP_DELETION_STATE];

export const TAB_CHANGE_STATE = {
  TAB_CHANGE_READY: 'TAB_CHANGE_READY',
  TAB_CHANGE_FINISHED: 'TAB_CHANGE_FINISHED',
  TAB_CHANGE_STARTED: 'TAB_CHANGE_STARTED',
} as const;
export type TabChangeState = typeof TAB_CHANGE_STATE[keyof typeof TAB_CHANGE_STATE];

const TAB_ACTION = {
  CHANGE_PREPARE: 'CHANGE_PREPARE',
  CHANGE: 'CHANGE',
  CHANGED: 'CHANGED',
  RENAME: 'RENAME',
  INIT: 'INIT',
} as const;
type TabAction =
  | {
      type: typeof TAB_ACTION.CHANGE;
      payload: Group;
    }
  | {
      type: typeof TAB_ACTION.CHANGED;
    }
  | {
      type: typeof TAB_ACTION.RENAME;
      payload: string;
    }
  | {
      type: typeof TAB_ACTION.CHANGE_PREPARE;
    }
  | {
      type: typeof TAB_ACTION.INIT;
      payload: Group[];
    };

type TabState = {
  currentGroup: Group;
  changeState: TabChangeState;
};

const tabReducer = (state: TabState, action: TabAction): TabState => {
  if (action.type === TAB_ACTION.CHANGE)
    return {
      currentGroup: action.payload,
      changeState: TAB_CHANGE_STATE.TAB_CHANGE_STARTED,
    };
  if (action.type === TAB_ACTION.CHANGED)
    return {
      ...state,
      changeState: TAB_CHANGE_STATE.TAB_CHANGE_FINISHED,
    };
  if (action.type === TAB_ACTION.RENAME) {
    // 未分類の場合は名前を変更できない
    if (!isCategorized(state.currentGroup)) return state;
    const newGroup = {
      ...state.currentGroup,
      name: action.payload,
    };
    return { ...state, currentGroup: newGroup };
  }
  if (action.type === TAB_ACTION.CHANGE_PREPARE) {
    return { ...state, changeState: TAB_CHANGE_STATE.TAB_CHANGE_READY };
  }
  if (action.type === TAB_ACTION.INIT) {
    if (!isUnnamedGroup(state.currentGroup)) return state;
    const group = action.payload.find(
      group => group.id.id === state.currentGroup.id.id
    );
    if (group)
      return {
        ...state,
        currentGroup: group,
        changeState: TAB_CHANGE_STATE.TAB_CHANGE_STARTED,
      };
    return {
      ...state,
      currentGroup: UncategorizedGroup,
      changeState: TAB_CHANGE_STATE.TAB_CHANGE_STARTED,
    };
  }
  return state;
};

type GroupingProps = Record<string, unknown>;

const Grouping: React.FC<GroupingProps> = props => {
  const { setIsShowMenu } = useGlobalMenuContext();
  useEffect(() => {
    return setIsShowMenu(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [groupingState, { getGroupingAPI, setGrouping }] = useGrouping();
  const initialGroup = useUnnamedGroup(groupingState);
  const [
    { currentGroup, changeState: tabChangeState },
    dispatchCurrentGroupState,
  ] = useReducer(tabReducer, {
    currentGroup: initialGroup ?? UncategorizedGroup,
    changeState: TAB_CHANGE_STATE.TAB_CHANGE_STARTED,
  });
  const [assetsToShow, setAssetsToShow] = useState<Asset[]>([]);
  useCustomHeaderTitle('グループ編集');
  const routing = useRouting();
  // 新規追加とグループ削除と初期化したときだけオンになる
  const [isScroll, setIsScroll] = useState<boolean>(true);
  const [groupDeletionState, setGroupDeletionState] = useState<
    GroupDeletionState
  >(GROUP_DELETION_STATE.GROUP_DELETE_READY);

  const apiCallGoBack = (): void => {
    routing.backRouting(true);
  };

  const [isWaitingGoBack, setIsWaitingGoBack] = useState<boolean>(false);
  useEffect(() => {
    if (
      groupDeletionState === GROUP_DELETION_STATE.GROUP_DELETE_READY &&
      isWaitingGoBack &&
      groupingState.changingState.length === 0
    ) {
      apiCallGoBack();
      return () => {
        setIsWaitingGoBack(false);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupDeletionState, isWaitingGoBack, groupingState.changingState]);

  useEffect(() => {
    if (isLoaded(groupingState)) {
      dispatchCurrentGroupState({
        type: TAB_ACTION.INIT,
        payload: groupingState.group.groups,
      });
      setAssetsToShow(
        groupingState.assets.filter(
          asset => asset.groupId.id === currentGroup.id.id
        )
      );
    }
  }, [groupingState, currentGroup.id.id]);

  const prevGroupNum = usePrevious(groupingState.group.groups.length);

  function animate(time: DOMHighResTimeStamp): void {
    requestAnimationFrame(animate);
    TWEEN.update(time);
  }

  useEffect(() => {
    if (tabChangeState === TAB_CHANGE_STATE.TAB_CHANGE_FINISHED) {
      dispatchCurrentGroupState({ type: TAB_ACTION.CHANGE_PREPARE });
    }
  }, [tabChangeState]);

  useEffect(() => {
    getGroupingAPI();
    const id = requestAnimationFrame(animate);
    return (): void => {
      cancelAnimationFrame(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 名前の変更を監視し、APIを呼び出す
    setGrouping(prev => {
      if (isLoading(prev) || isUnloaded(prev)) return prev;

      const groups: Group[] = prev.group.groups.map(group => {
        if (group.id.id === currentGroup.id.id) return currentGroup;
        return group;
      });
      return {
        ...prev,
        group: {
          ...prev.group,
          groups: groups,
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroup.name]);

  useEffect(() => {
    // グループの数が減ったら、localのdeleteが終わった
    if (!prevGroupNum) return;
    if (prevGroupNum > groupingState.group.groups.length)
      setGroupDeletionState(GROUP_DELETION_STATE.GROUP_DELETE_FINISHED);
  }, [groupingState.group.groups.length, prevGroupNum]);

  const editGroupName = (newName: string): void => {
    dispatchCurrentGroupState({ type: TAB_ACTION.RENAME, payload: newName });
  };

  useEffect(() => {
    // グループを削除する関数を閉じ込めることで、削除するにはGroupDeletionStateを操作するしかなくなる
    const deleteGroup = async (): Promise<void> => {
      await setGrouping(prev => {
        if (isLoading(prev) || isUnloaded(prev)) return prev;

        const groups = prev.group.groups.filter(
          group => group.id.id !== currentGroup.id.id
        );
        // groupの紐付けがなくなったassetは、全て未分類になる
        const assets = prev.assets.map(asset => {
          if (asset.groupId.id === currentGroup.id.id)
            return { ...asset, groupId: UncategorizedGroup.id };
          return asset;
        });
        return {
          ...prev,
          group: {
            ...prev.group,
            groups: groups,
          },
          assets: assets,
        };
      });
    };

    if (
      groupDeletionState ===
      GROUP_DELETION_STATE.GROUP_DELETE_ANIMATION_FINISHED
    ) {
      (async () => {
        await deleteGroup();
      })();
      setIsScroll(true);
    }
    if (groupDeletionState === GROUP_DELETION_STATE.GROUP_DELETE_FINISHED) {
      setGroupDeletionState(GROUP_DELETION_STATE.GROUP_DELETE_READY);
      // さらにアニメーションが終了した時に、未分類に戻す
      dispatchCurrentGroupState({
        type: TAB_ACTION.CHANGE,
        payload: UncategorizedGroup,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setGrouping, groupDeletionState]);

  const onClickDeleteGroupHandler = (): void => {
    if (isScroll) return;
    setGroupDeletionState(GROUP_DELETION_STATE.GROUP_DELETE_ANIMATION_STARTED);
  };
  const changeGroup = (assetIds: AssetId[], groupId: GroupId): void => {
    setGrouping(prev => {
      if (isLoading(prev) || isUnloaded(prev)) return prev;

      const assets = prev.assets.map(asset => {
        if (assetIds.includes(asset.id)) return { ...asset, groupId: groupId };
        return asset;
      });
      return {
        ...prev,
        assets: assets,
      };
    });
  };
  const onClickChangeGroupHandler = (
    assetIds: AssetId[],
    groupId: GroupId
  ): void => {
    if (isScroll) return;
    changeGroup(assetIds, groupId);
  };
  const addGroup = async (): Promise<void> => {
    setGrouping(prev => {
      if (isLoading(prev) || isUnloaded(prev)) return prev;
      const categorizedGroups = getCategorizedGroups(prev.group.groups);
      if (categorizedGroups.length >= 10) return prev;

      const newGroupTabNums: number[] = Array.from(
        Array(10),
        (_, k) => k + 1
      ).filter(num => {
        return !categorizedGroups.map(group => group.tabNum).includes(num);
      });
      const newGroupTabNum: number = newGroupTabNums.length
        ? newGroupTabNums[0]
        : 1;
      const newGroupName = `グループ${newGroupTabNum}`;
      const prevGroupNames = prev.group.groups.map(group => group.name);
      const newGroupId = undecidedId(newGroupTabNum);

      const name = ((newGroupName: string): string => {
        let ret = newGroupName;
        while (prevGroupNames.includes(ret)) {
          const regex = /^(.*)\((\d+)\)$/;
          const result = regex.exec(ret);
          if (result) {
            ret = ret.replace(regex, `${result[1]}(${Number(result[2]) + 1})`);
          } else {
            ret = `${ret}(1)`;
          }
        }
        return ret;
      })(newGroupName);
      return {
        ...prev,
        group: {
          groups: [
            ...prev.group.groups,
            {
              id: newGroupId,
              tabNum: newGroupTabNum,
              name: name,
              targetGoal: null,
              createdAt: new Date(),
            },
          ],
        },
      };
    });
  };

  const onFinishGroupDeleteAnimation = useCallback(() => {
    setGroupDeletionState(GROUP_DELETION_STATE.GROUP_DELETE_ANIMATION_FINISHED);
  }, [setGroupDeletionState]);

  const finishTabChange = useCallback(() => {
    dispatchCurrentGroupState({
      type: TAB_ACTION.CHANGED,
    });
  }, [dispatchCurrentGroupState]);

  const setCurrentGroup = useCallback(
    (group: Group) => {
      if (isScroll) return;
      if (groupDeletionState !== GROUP_DELETION_STATE.GROUP_DELETE_READY)
        return;
      dispatchCurrentGroupState({
        type: TAB_ACTION.CHANGE,
        payload: group,
      });
    },
    [isScroll, dispatchCurrentGroupState, groupDeletionState]
  );

  if (isLoading(groupingState) || isUnloaded(groupingState))
    return (
      <Loading
        loading={true}
        style={{
          backgroundColor: 'transparent',
          paddingTop: '100px',
        }}
      />
    );

  const groupClass = (group: Group): string =>
    !isCategorized(group)
      ? 'unclassified'
      : `group${group.tabNum.toString().padStart(2, '0')}`;

  function handleClickSecurityaccounts(): void {
    routing.pushRouting({ path: '/securityaccounts' }, true);
  }

  return (
    <>
      <div className="mt-4 mx-2">
        <div className="flex justify-end items-baseline mx-1 pb-3">
          <div className="flex text-link">
            <div
              className="text-sm mx-1 cursor-pointer"
              onClick={handleClickSecurityaccounts}>
              口座一覧
            </div>
          </div>
        </div>
      </div>
      <div className="grouping-tab-block">
        <GroupingTabList
          groups={groupingState.group.groups}
          assets={groupingState.assets}
          currentGroup={currentGroup}
          setCurrentGroup={setCurrentGroup}
          addGroup={addGroup}
          groupClass={groupClass}
          isTabChanging={tabChangeState === TAB_CHANGE_STATE.TAB_CHANGE_STARTED}
          isScroll={isScroll}
          setIsScroll={setIsScroll}
          groupDeletionState={groupDeletionState}
          onFinishGroupDeleteAnimation={onFinishGroupDeleteAnimation}
        />
        <div className={`grouping-tab-body ${groupClass(currentGroup)}`}>
          <div className="grouping-tab-body-item">
            <GroupingTabSection
              groupFilteredAssets={assetsToShow}
              currentGroup={currentGroup}
              finishTabChange={finishTabChange}
              tabChangeState={tabChangeState}
              groups={groupingState.group.groups}
              editGroupName={editGroupName}
              deleteGroup={onClickDeleteGroupHandler}
              changeGroup={onClickChangeGroupHandler}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Grouping;
